// ポインタ（マウス/タッチ）→ グリッドセルの変換。
//
// 画面座標を NDC に直し、カメラから pickPlane（庭面）へ raycast して交点を得る。
// 交点を island ローカル空間に変換してからグリッド座標に落とす（island は自転
// するので、ワールド座標のままでは正しいセルにならない）。
//
// タッチを一級市民として扱う: PointerEvent に統一し、マウスもタッチも同じ経路。

import * as THREE from 'three';
import { worldToGrid } from './diorama';

export interface Picker {
  /**
   * クライアント座標（イベントの clientX/clientY）からグリッドセルを得る。
   * 庭面の外を指していれば null。
   */
  pick(clientX: number, clientY: number): { gx: number; gy: number } | null;
  /**
   * クライアント座標がカフェ本体（メッシュ群）を指しているか。
   * カフェタップ→メニューを開くために使う。庭面のヒットより手前にあるかは見ない
   * （カフェは庭の奥に立ち、俯瞰では重ならないので、当たれば即カフェ扱いでよい）。
   */
  pickCafe(clientX: number, clientY: number): boolean;
}

/**
 * @param domElement レンダラのキャンバス（座標基準）。
 * @param camera     シーンのカメラ。
 * @param pickPlane  庭面のピッキング平面（island の子）。
 * @param cafe       カフェ本体の Group（タップ判定の対象）。
 */
export function createPicker(
  domElement: HTMLElement,
  camera: THREE.Camera,
  pickPlane: THREE.Object3D,
  cafe?: THREE.Object3D,
): Picker {
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  /** クライアント座標を NDC に直して raycaster をセットする（共通前処理）。 */
  function setRay(clientX: number, clientY: number): boolean {
    const rect = domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    return true;
  }

  function pick(clientX: number, clientY: number): { gx: number; gy: number } | null {
    if (!setRay(clientX, clientY)) return null;
    const hits = raycaster.intersectObject(pickPlane, false);
    if (hits.length === 0) return null;

    // 交点（ワールド）を pickPlane の親（island）ローカルへ変換する。
    const local = pickPlane.parent
      ? pickPlane.parent.worldToLocal(hits[0].point.clone())
      : hits[0].point.clone();
    return worldToGrid(local.x, local.z);
  }

  function pickCafe(clientX: number, clientY: number): boolean {
    if (!cafe) return false;
    if (!setRay(clientX, clientY)) return false;
    return raycaster.intersectObject(cafe, true).length > 0;
  }

  return { pick, pickCafe };
}
